import * as analytics from "@/lib/analytics";
import type {
  SendMessageActionOptions,
  SendMessageMultichannelActionOptions,
} from "@/api/types/automation";
import type { FlowNodeAction, FlowNodeDefinition } from "@/api/types/flow";
import type { ViewMode } from "@/components/ui/view-toggle";

/**
 * Flows index funnel: Flows Index Viewed → Flow Playbook Highlight
 * Clicked (review an inactive playbook). Tag and view changes explain
 * how people scan the list.
 */
export function trackFlowsIndexViewed(props: { gym: string; view: ViewMode }) {
  analytics.track("Flows Index Viewed", props);
}

export function trackFlowPlaybookHighlightClicked(props: {
  gym: string;
  flow: string;
  tag: string;
  people: number;
}) {
  analytics.track("Flow Playbook Highlight Clicked", props);
}

export function trackFlowTagFilterChanged(props: { gym: string; tag: string | null }) {
  analytics.track("Flow Tag Filter Changed", props);
}

export function trackFlowViewToggled(props: { gym: string; view: ViewMode }) {
  analytics.track("Flow View Toggled", props);
}

/**
 * PostHog events for the flow editor. The build funnel is: Flow Step Added ->
 * Flow Step Updated -> Flow Status Updated (emitted by the activation button),
 * broken down by `step` (node type) and `action` (first action type) so each
 * kind of step can be compared for adoption and completion.
 *
 * Every event carries `gym` (the gym name) and `flow` (the flow name),
 * matching Flow Status Updated.
 */

/** How a step landed on the canvas, for comparing add affordances. */
export type StepAddMethod = "insert" | "palette" | "duplicate";

interface StepProps {
  /** Node type: action, timer, filter, conditional, branch. */
  step: string;
  /** First action type for action steps (e.g. `outgoing_message`). */
  action?: string;
  /** `sms` or `email` for single-channel message steps. */
  protocol?: string;
  /** Text-or-email steps: what happens when neither channel is reachable. */
  fallback?: "task" | "skip";
  /** Text-or-email steps: whether the text variant carries attachments. */
  hasMedia?: boolean;
  /** Text-or-email steps: whether the email variant sets preview text. */
  hasPreviewText?: boolean;
}

/** Flattens a node definition into the step properties every event carries. */
export function describeStep(definition: FlowNodeDefinition): StepProps {
  const props: StepProps = { step: definition.type };
  if (definition.type !== "action") return props;
  const action = (definition.options as FlowNodeAction["options"] | undefined)?.actions?.[0];
  if (!action) return props;
  props.action = action.type;
  if (action.type === "outgoing_message") {
    props.protocol = (action.options as SendMessageActionOptions).protocol ?? "sms";
  }
  if (action.type === "outgoing_message_multichannel") {
    const options = action.options as Partial<SendMessageMultichannelActionOptions>;
    props.fallback = options.fallbackTask ? "task" : "skip";
    props.hasMedia = (options.sms?.media?.length ?? 0) > 0;
    props.hasPreviewText = !!options.email?.previewText?.trim();
  }
  return props;
}

export function trackStepAdded(props: {
  gym: string;
  flow: string;
  definition: FlowNodeDefinition;
  method: StepAddMethod;
}) {
  analytics.track("Flow Step Added", {
    gym: props.gym,
    flow: props.flow,
    method: props.method,
    ...describeStep(props.definition),
  });
}

export function trackStepUpdated(props: {
  gym: string;
  flow: string;
  definition: FlowNodeDefinition;
}) {
  analytics.track("Flow Step Updated", {
    gym: props.gym,
    flow: props.flow,
    ...describeStep(props.definition),
  });
}

export function trackStepRemoved(props: { gym: string; flow: string }) {
  analytics.track("Flow Step Removed", { gym: props.gym, flow: props.flow });
}
